/**
 * Bark API endpoints for distribution tracking.
 */

import { api } from './client';
import type {
  Bark,
  BarkItem,
  BarkWithItems,
  BarkSummary,
  CreateBarkRequest,
  ScheduleBarkRequest,
  AddBarkItemRequest,
  MarkSentRequest,
  MarkFailedRequest,
  MarkSkippedRequest,
  PlatformConnection,
  ApiResponse,
  AvailablePlatform,
  CreatePlatformConnectionRequest,
  BarkItemWithContent,
  QueueParams,
} from '../types';
import type { TestConnectionResponse } from './databaseConnections';

const siteOptions = (siteSlug?: string) => siteSlug ? { headers: { 'x-wpp-site-slug': siteSlug } } : undefined;

/** Barks API */
export const barksApi = {
  /** List barks for a specific content item */
  listForContent: (contentType: string, contentId: string, siteSlug?: string) =>
    api.get<ApiResponse<Bark[]>>(`/v1/content/${contentType}/${contentId}/barks`, siteOptions(siteSlug)),

  /** Get a bark with all its items */
  get: (barkId: string, siteSlug?: string) =>
    api.get<ApiResponse<BarkWithItems>>(`/v1/barks/${barkId}`, siteOptions(siteSlug)),

  /** Create a new bark */
  create: (data: CreateBarkRequest, siteSlug?: string) =>
    api.post<ApiResponse<Bark>>('/v1/barks', data, siteOptions(siteSlug)),

  /** Atomically schedule one Bark with exact per-connection instants. */
  schedule: (data: ScheduleBarkRequest, siteSlug?: string) =>
    api.post<ApiResponse<BarkWithItems>>('/v1/barks', data, siteOptions(siteSlug)),

  rescheduleItem: (itemId: string, scheduledFor: string | null, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/items/${itemId}/schedule`, { scheduled_for: scheduledFor }, siteOptions(siteSlug)),

  /** Delete a bark and all its items */
  delete: (barkId: string, siteSlug?: string) =>
    api.delete<ApiResponse<null>>(`/v1/barks/${barkId}`, undefined, siteOptions(siteSlug)),

  /** Update bark name */
  updateName: (barkId: string, name: string | null, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/${barkId}/name`, { name }, siteOptions(siteSlug)),

  /** Get bark summary stats */
  getSummary: (barkId: string, siteSlug?: string) =>
    api.get<ApiResponse<BarkSummary>>(`/v1/barks/${barkId}/summary`, siteOptions(siteSlug)),

  /** Add an item to a bark */
  addItem: (barkId: string, data: AddBarkItemRequest, siteSlug?: string) =>
    api.post<ApiResponse<BarkItem>>(`/v1/barks/${barkId}/items`, data, siteOptions(siteSlug)),

  /** Mark item as sent */
  markItemSent: (itemId: string, data: MarkSentRequest, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/items/${itemId}/sent`, data, siteOptions(siteSlug)),

  /** Mark item as failed */
  markItemFailed: (itemId: string, data: MarkFailedRequest, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/items/${itemId}/failed`, data, siteOptions(siteSlug)),

  /** Mark item as skipped */
  markItemSkipped: (itemId: string, data: MarkSkippedRequest, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/items/${itemId}/skip`, data, siteOptions(siteSlug)),

  /** Reset item to pending */
  resetItem: (itemId: string, siteSlug?: string) =>
    api.put<ApiResponse<null>>(`/v1/barks/items/${itemId}/reset`, {}, siteOptions(siteSlug)),

  /** Delete a single item */
  deleteItem: (itemId: string, siteSlug?: string) =>
    api.delete<ApiResponse<null>>(`/v1/barks/items/${itemId}`, undefined, siteOptions(siteSlug)),

  /** List queue items for a site */
  listQueue: (siteSlug: string, params?: QueueParams) => {
    const queryParts: string[] = [];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.offset) queryParts.push(`offset=${params.offset}`);
    if (params?.status) queryParts.push(`status=${params.status}`);
    if (params?.from) queryParts.push(`from=${encodeURIComponent(params.from)}`);
    if (params?.to) queryParts.push(`to=${encodeURIComponent(params.to)}`);
    const query = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return api.get<ApiResponse<BarkItemWithContent[]>>(`/v1/sites/${siteSlug}/bark/queue${query}`);
  },
};

/** Platform Connections API */
export const platformConnectionsApi = {
  /** List all platform connections */
  list: (siteSlug?: string) =>
    api.get<ApiResponse<PlatformConnection[]>>(siteSlug ? `/v1/platforms?site_slug=${encodeURIComponent(siteSlug)}` : '/v1/platforms'),

  /** List available platforms */
  listAvailable: () =>
    api.get<ApiResponse<AvailablePlatform[]>>('/v1/platforms/available'),

  /** Create a new platform connection */
  create: (data: CreatePlatformConnectionRequest) =>
    api.post<ApiResponse<PlatformConnection>>('/v1/platforms', data),

  /** Delete a platform connection */
  delete: (id: number) =>
    api.delete<ApiResponse<null>>(`/v1/platforms/${id}`),

  /** Test a platform connection */
  test: (id: number) =>
    api.post<ApiResponse<TestConnectionResponse>>(`/v1/platforms/${id}/test`, {}),
};
